import clsx from 'clsx';
import { isEmpty } from 'lodash';
import { useMemo } from 'react';
import { useAppContext } from 'src/contexts/AppContext';
import { Content } from 'src/contexts/ContentContext';
import { useOnSaleEventsContext } from 'src/contexts/OnSaleEventsContext/OnSaleEventsContext';
import { useSiteTheme } from 'src/contexts/SiteTheme/SiteThemeContext';
import { vars } from 'src/core/themes';
import { Stack } from 'src/core/ui';
import { useUserHasAnyOfPermissions } from 'src/hooks/useUserHasAnyOfPermissions';
import { ArrowDownIcon7, ArrowUpIcon7 } from 'src/svgs/Viagogo';
import {
  addValueMultipleTimes,
  calculatePercentile,
} from 'src/utils/arrayUtils';
import { ContentId } from 'src/utils/constants/contentId';
import { applyNumberFormatting } from 'src/utils/numberFormatter';
import {
  EventSectionMetrics,
  SectionInfo,
  UiMoney,
} from 'src/WebApiController';
import { Permission } from 'src/WebApiController';

import * as styles from './OnSaleEventDetails.css';

const toTransactionPrices = (metrics: EventSectionMetrics[]) => {
  const curMin: number[] = [],
    preMin: number[] = [],
    curP25: number[] = [],
    preP25: number[] = [],
    curP50: number[] = [],
    preP50: number[] = [];

  for (const { transactions, minPrice, medianPrice, p25Price } of metrics) {
    const curTrans = transactions?.currentMetricsValue ?? 0;
    const preTrans = transactions?.previousMetricsValue ?? 0;
    if (curTrans > 0) {
      if (minPrice?.currentMetricsValue?.amt > 0) {
        addValueMultipleTimes(
          curMin,
          minPrice.currentMetricsValue.amt,
          curTrans
        );
      }
      if (p25Price?.currentMetricsValue?.amt > 0) {
        addValueMultipleTimes(
          curP25,
          p25Price.currentMetricsValue.amt,
          curTrans
        );
      }
      if (medianPrice?.currentMetricsValue?.amt > 0) {
        addValueMultipleTimes(
          curP50,
          medianPrice.currentMetricsValue.amt,
          curTrans
        );
      }
    }
    if (preTrans > 0) {
      if (minPrice?.previousMetricsValue?.amt > 0) {
        addValueMultipleTimes(
          preMin,
          minPrice.previousMetricsValue.amt,
          preTrans
        );
      }
      if (p25Price?.previousMetricsValue?.amt > 0) {
        addValueMultipleTimes(
          preP25,
          p25Price.previousMetricsValue.amt,
          preTrans
        );
      }
      if (medianPrice?.previousMetricsValue?.amt > 0) {
        addValueMultipleTimes(
          preP50,
          medianPrice.previousMetricsValue.amt,
          preTrans
        );
      }
    }
  }

  return {
    min: {
      current: calculatePercentile(curMin, 0),
      previous: calculatePercentile(preMin, 0),
    },
    p25: {
      current: calculatePercentile(curP25, 25),
      previous: calculatePercentile(preP25, 25),
    },
    p50: {
      current: calculatePercentile(curP50, 50),
      previous: calculatePercentile(preP50, 50),
    },
  };
};

export const OnSaleEventPriceTracker = ({
  selectedSections,
}: {
  selectedSections: SectionInfo[];
}) => {
  const { sectionMetrics } = useOnSaleEventsContext();
  const { isDarkMode } = useSiteTheme();
  const hasManageEventDiscoveryPermission = useUserHasAnyOfPermissions(
    Permission.Events_ManageEventDiscovery
  );

  const { loginContext } = useAppContext();
  const defaultCurrency =
    loginContext?.user?.activeAccount?.currencyCode ?? 'USD';

  const filteredSectionMetrics = useMemo(() => {
    if (isEmpty(selectedSections)) {
      return sectionMetrics;
    }
    const selectedSectionIds = selectedSections.map(({ id }) => id);
    return sectionMetrics.filter(({ sectionId }) =>
      selectedSectionIds.includes(sectionId)
    );
  }, [selectedSections, sectionMetrics]);

  const eventPriceTrackes = useMemo(() => {
    const sectionWithTransactions = filteredSectionMetrics.filter(
      ({ transactions }) =>
        (transactions?.currentMetricsValue ?? 0) > 0 ||
        (transactions?.previousMetricsValue ?? 0) > 0
    );

    const prices = toTransactionPrices(sectionWithTransactions);
    return [
      {
        contnetId: ContentId.MinimumPrice,
        currentValue: {
          amt: prices.min.current ?? 0,
          currency: defaultCurrency,
        },
        previousValue: {
          amt: prices.min.previous ?? 0,
          currency: defaultCurrency,
        },
      },
      {
        contnetId: ContentId.LowerRangePrice,
        currentValue: {
          amt: prices.p25.current ?? 0,
          currency: defaultCurrency,
        },
        previousValue: {
          amt: prices.p25.previous ?? 0,
          currency: defaultCurrency,
        },
      },
      {
        contnetId: ContentId.MedianPrice,
        currentValue: {
          amt: prices.p50.current ?? 0,
          currency: defaultCurrency,
        },
        previousValue: {
          amt: prices.p50.previous ?? 0,
          currency: defaultCurrency,
        },
      },
    ];
  }, [defaultCurrency, filteredSectionMetrics]);

  return (
    <Stack
      width="full"
      direction="column"
      className={clsx(styles.onSaleEventPriceTrackerContainer, {
        [styles.onSaleEventPriceTrackerContainerDark]: isDarkMode,
      })}
    >
      {eventPriceTrackes.map(({ contnetId, currentValue, previousValue }) => (
        <Stack
          key={contnetId}
          direction="row"
          width="full"
          className={styles.onSaleEventPriceTrackerItem}
        >
          <div
            className={clsx(
              styles.onSaleEventPriceTrackerItemCell,
              styles.onSaleEventPriceTrackerItemCellLabel
            )}
          >
            <Content id={contnetId} />
          </div>
          <div
            className={clsx(
              styles.onSaleEventPriceTrackerItemCell,
              styles.onSaleEventPriceTrackerItemCellValue,
              !hasManageEventDiscoveryPermission &&
                styles.onSaleEventPriceTrackerItemCellBlurred
            )}
          >
            <IncrementalPricesCell
              currentPrice={currentValue}
              previousPrice={previousValue}
            />
          </div>
        </Stack>
      ))}
    </Stack>
  );
};

enum PriceComparisonResult {
  Increase,
  Equal,
  Decrease,
}

const IncrementalPricesCell = ({
  currentPrice,
  previousPrice,
}: {
  currentPrice: Pick<UiMoney, 'amt' | 'currency'> | undefined;
  previousPrice: Pick<UiMoney, 'amt' | 'currency'> | undefined;
}) => {
  const [priceCheckResult, adbDiffPrice] = useMemo(() => {
    const priceDiff = (currentPrice?.amt ?? 0) - (previousPrice?.amt ?? 0);

    const diffResult =
      priceDiff === 0
        ? PriceComparisonResult.Equal
        : priceDiff > 0
          ? PriceComparisonResult.Increase
          : PriceComparisonResult.Decrease;

    return [diffResult, { ...currentPrice, amt: Math.abs(priceDiff) }];
  }, [currentPrice, previousPrice?.amt]);

  return (
    <Stack
      direction="row"
      width="full"
      alignItems="center"
      justifyContent="spaceBetween"
    >
      <div style={{ flex: 1 }}>
        {applyNumberFormatting({
          inputNumber: currentPrice?.amt,
          currencyCode: currentPrice?.currency,
        })}
      </div>

      <Stack
        width="full"
        gap="s"
        alignItems="center"
        justifyContent="end"
        style={{ flex: 1 }}
        className={clsx(styles.onSaleEventPriceTrackerComparisonPrice, {
          [styles.onSaleEventPriceTrackerComparisonPriceIncrease]:
            priceCheckResult === PriceComparisonResult.Increase,
        })}
      >
        <span>
          {priceCheckResult === PriceComparisonResult.Increase && (
            <ArrowUpIcon7 size={vars.iconSize.xs} />
          )}
          {priceCheckResult === PriceComparisonResult.Decrease && (
            <ArrowDownIcon7 size={vars.iconSize.xs} />
          )}
        </span>
        {applyNumberFormatting({
          inputNumber: adbDiffPrice?.amt ?? 0,
          currencyCode: adbDiffPrice?.currency,
        })}
      </Stack>
    </Stack>
  );
};
